<script setup lang="ts">
/**
 * A marker at a point of a plan — a device on a floor plan, a desk, a spot on
 * a site map. It sits in `NxPlanCanvas`'s `markers` slot at the screen
 * `position` the slot's `worldToScreen` gives, centred on the point, and is a
 * real button: focusable, a label, a click. It keeps its size at every zoom.
 *
 * The look follows `NxControlTile`: an icon disc, `active` (on / open)
 * fills it in the accent — brand, or the `tone` when one is set — and a
 * `tone` shows even while inactive (an open window in `warning`);
 * `unavailable` (unreachable) draws it dashed and muted; `loading` swaps the
 * icon for a spinner; `selected` rings it. `caption` puts a short read-out
 * beside it (a temperature). `occluded` dims it while something in front
 * hides its point (a wall in a 3D view); it stays a button.
 *
 * With a `popover` slot the click opens a popover at the marker
 * (`v-model:open`), for its quick actions. `passive` makes it a picture only:
 * presses fall through to the plan (an editor's tools pick it themselves) and
 * it leaves the tab order.
 */
import { computed, type Component } from 'vue';
import { NxSpinner } from '../Loading';
import { accentVars, type NxAccentTone } from '../Panel/accent';
import { NxPopover, type NxPopoverSide } from '../Popover';
import type { NxPlanPoint } from './geometry';

const props = withDefaults(
  defineProps<{
    /** Screen position in the markers layer (px), usually `worldToScreen(point)`. */
    position: NxPlanPoint;
    /** Accessible name and tooltip („Haustür · Verriegelt“). */
    label: string;
    icon?: Component;
    tone?: NxAccentTone;
    active?: boolean;
    unavailable?: boolean;
    selected?: boolean;
    loading?: boolean;
    /** Something in front hides the marked point: the marker is dimmed, still operable. */
    occluded?: boolean;
    /** Short read-out beside the marker. */
    caption?: string;
    /** A picture only: no presses, not focusable. */
    passive?: boolean;
    size?: 'sm' | 'md';
    /** Accessible name of the popover. */
    popoverLabel?: string;
    /** Preferred side of the popover. */
    popoverSide?: NxPopoverSide;
  }>(),
  {
    icon: undefined,
    tone: 'neutral',
    active: false,
    unavailable: false,
    selected: false,
    loading: false,
    occluded: false,
    caption: undefined,
    passive: false,
    size: 'md',
    popoverLabel: undefined,
    popoverSide: 'top',
  }
);

const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{ click: [event: MouseEvent] }>();

defineSlots<{
  /** Replaces the icon in the disc. */
  icon?: () => unknown;
  /** Content of the popover the marker opens. */
  popover?: () => unknown;
}>();

const hasTone = computed(() => props.tone !== 'neutral');
const style = computed(() => ({
  transform: `translate(${props.position.x}px, ${props.position.y}px)`,
  ...accentVars(hasTone.value ? props.tone : 'brand', null, '--marker-accent'),
}));
</script>

<template>
  <div
    class="nx-plan-marker"
    :class="[
      `size-${props.size}`,
      {
        'has-tone': hasTone,
        'is-active': props.active && !props.unavailable,
        'is-unavailable': props.unavailable,
        'is-selected': props.selected,
        'is-occluded': props.occluded,
        'is-passive': props.passive,
      },
    ]"
    :style="style"
    data-testid="nx-plan-marker"
  >
    <span class="nx-plan-marker__spot">
      <NxPopover
        v-if="$slots.popover && !props.passive"
        :open="open"
        :side="props.popoverSide"
        :label="props.popoverLabel ?? props.label"
        lazy
        arrow
        @update:open="open = $event"
      >
        <template #trigger>
          <button
            type="button"
            class="nx-plan-marker__pin"
            :aria-label="props.label"
            :title="props.label"
            :aria-busy="props.loading || undefined"
            data-testid="nx-plan-marker-pin"
            @click="emit('click', $event)"
          >
            <span class="nx-plan-marker__disc" aria-hidden="true">
              <NxSpinner v-if="props.loading" size="sm" />
              <slot v-else name="icon">
                <component :is="props.icon" v-if="props.icon" class="nx-plan-marker__icon" />
              </slot>
            </span>
          </button>
        </template>
        <slot name="popover" />
      </NxPopover>
      <button
        v-else
        type="button"
        class="nx-plan-marker__pin"
        :aria-label="props.label"
        :title="props.label"
        :aria-busy="props.loading || undefined"
        :tabindex="props.passive ? -1 : undefined"
        data-testid="nx-plan-marker-pin"
        @click="emit('click', $event)"
      >
        <span class="nx-plan-marker__disc" aria-hidden="true">
          <NxSpinner v-if="props.loading" size="sm" />
          <slot v-else name="icon">
            <component :is="props.icon" v-if="props.icon" class="nx-plan-marker__icon" />
          </slot>
        </span>
      </button>
    </span>
    <span v-if="props.caption" class="nx-plan-marker__caption" aria-hidden="true">
      {{ props.caption }}
    </span>
  </div>
</template>

<style scoped>
/* --marker-accent* come from accentVars: the tone, else brand. */
.nx-plan-marker {
  --marker-size: var(--nxt-control-size-sm);
  --marker-bg: var(--nxt-surface-raised-bg);
  --marker-fg: var(--nxt-fg-subtle);
  --marker-edge: var(--nxt-border-strong);

  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  pointer-events: auto;
}
.nx-plan-marker.size-sm {
  --marker-size: var(--nxt-control-size-xs);
}
.nx-plan-marker.has-tone {
  --marker-bg: color-mix(in srgb, var(--marker-accent) 16%, var(--nxt-surface-raised-bg));
  --marker-fg: var(--marker-accent);
  --marker-edge: var(--marker-accent-border);
}
.nx-plan-marker.is-active {
  --marker-bg: var(--marker-accent);
  --marker-fg: var(--marker-accent-on);
  --marker-edge: var(--marker-accent);
}
.nx-plan-marker.is-unavailable {
  --marker-bg: var(--nxt-surface-sunken-bg);
  --marker-fg: var(--nxt-disabled-fg);
  --marker-edge: var(--nxt-border-strong);
}
.nx-plan-marker.is-passive {
  pointer-events: none;
}
.nx-plan-marker.is-occluded {
  opacity: 0.45;
}

/* The pin's box, centred on the point; the popover's anchor fills it, so the
   popover points at the pin, not at its centre. */
.nx-plan-marker__spot {
  position: absolute;
  top: calc(var(--marker-size) / -2);
  left: calc(var(--marker-size) / -2);
  width: var(--marker-size);
  height: var(--marker-size);
}
.nx-plan-marker__spot > :deep(.nx-popover-anchor) {
  display: block;
  width: 100%;
  height: 100%;
}

.nx-plan-marker__pin {
  position: relative;
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--nxt-radius-full);
  background: none;
  color: inherit;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
}
/* A 44px touch target round the smaller disc. */
.nx-plan-marker__pin::before {
  position: absolute;
  inset: calc((var(--marker-size) - calc(var(--nxt-size-unit) * 2.75)) / 2);
  border-radius: var(--nxt-radius-full);
  content: '';
}
.nx-plan-marker.is-passive .nx-plan-marker__pin {
  cursor: inherit;
}
.nx-plan-marker__pin:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}

.nx-plan-marker__disc {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  border: calc(var(--nxt-hairline) * 1.5) solid var(--marker-edge);
  border-radius: var(--nxt-radius-full);
  background: var(--marker-bg);
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    var(--nxt-shadow-sm);
  color: var(--marker-fg);
  font-size: calc(var(--marker-size) * 0.55);
  transition:
    background-color var(--nxt-motion-base) var(--nxt-ease-in-out),
    color var(--nxt-motion-base) var(--nxt-ease-in-out),
    transform var(--nxt-motion-fast) var(--nxt-ease-out);
}
.nx-plan-marker.is-unavailable .nx-plan-marker__disc {
  border-style: dashed;
}
.nx-plan-marker:not(.is-passive) .nx-plan-marker__pin:hover .nx-plan-marker__disc {
  transform: scale(1.08);
}
.nx-plan-marker.is-selected .nx-plan-marker__disc {
  box-shadow:
    0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg),
    0 0 0 calc(var(--nxt-hairline) * 4) var(--nxt-brand-bg),
    var(--nxt-shadow-md);
}
.nx-plan-marker__icon,
.nx-plan-marker__disc :deep(svg) {
  width: 1em;
  height: 1em;
}

.nx-plan-marker__caption {
  position: absolute;
  top: calc(var(--marker-size) / 2 + var(--nxt-space-2xs));
  left: 0;
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-sm);
  background: color-mix(in srgb, var(--nxt-surface-raised-bg) 88%, transparent);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  font-weight: var(--nxt-font-weight-medium);
  line-height: var(--nxt-line-height-tight);
  white-space: nowrap;
  transform: translateX(-50%);
  pointer-events: none;
}
.nx-plan-marker.has-tone .nx-plan-marker__caption {
  color: var(--marker-accent);
}

@media (prefers-reduced-motion: reduce) {
  .nx-plan-marker__disc {
    transition: none;
  }
}
@media (forced-colors: active) {
  .nx-plan-marker__disc {
    border-color: CanvasText;
  }
  .nx-plan-marker.is-active .nx-plan-marker__disc {
    background: Highlight;
    color: HighlightText;
  }
}
</style>
